import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Prediction } from '../../types/predictions';
import { Theme } from '../../theme';
import { calculateFairLeaderboard, ScoringResult, PriceDataPoint } from '../../utils/leaderboardScoring';

const LeaderboardContainer = styled.div<{ $embedded?: boolean }>`
  background: ${props => props.$embedded ? 'transparent' : props.theme.surface};
  border-radius: ${props => props.$embedded ? '0' : '12px'};
  padding: ${props => props.$embedded ? '8px 0' : '16px'};
  margin: ${props => props.$embedded ? '0' : '8px 0'};
  border: ${props => props.$embedded ? 'none' : `1px solid ${props.theme.border}`};
`;

const LeaderboardHeader = styled.div<{ $hidden?: boolean }>`
  display: ${props => props.$hidden ? 'none' : 'flex'};
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const LeaderboardTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${props => props.theme.text};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LeaderboardSubtitle = styled.span`
  font-size: 12px;
  color: ${props => props.theme.subText};
  font-weight: 400;
`;

const LeaderRow = styled.div<{ rank: number }>`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  margin-bottom: 8px;
  background: ${props => {
    if (props.rank === 1) return 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 215, 0, 0.05) 100%)';
    if (props.rank === 2) return 'linear-gradient(135deg, rgba(192, 192, 192, 0.15) 0%, rgba(192, 192, 192, 0.05) 100%)';
    if (props.rank === 3) return 'linear-gradient(135deg, rgba(205, 127, 50, 0.15) 0%, rgba(205, 127, 50, 0.05) 100%)';
    return props.theme.background;
  }};
  border-radius: 8px;
  border-left: 3px solid ${props => {
    if (props.rank === 1) return '#FFD700';
    if (props.rank === 2) return '#C0C0C0';
    if (props.rank === 3) return '#CD7F32';
    return props.theme.border;
  }};
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateX(4px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const RankBadge = styled.div<{ rank: number }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 12px;
  margin-right: 12px;
  flex-shrink: 0;
  background: ${props => {
    if (props.rank === 1) return 'linear-gradient(135deg, #FFD700, #FFA500)';
    if (props.rank === 2) return 'linear-gradient(135deg, #C0C0C0, #808080)';
    if (props.rank === 3) return 'linear-gradient(135deg, #CD7F32, #8B4513)';
    return props.theme.border;
  }};
  color: ${props => props.rank <= 3 ? '#fff' : props.theme.text};
`;

const ProfileImage = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 10px;
  flex-shrink: 0;
  border: 2px solid ${props => props.theme.border};
`;

const ProfilePlaceholder = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.theme.background};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  margin-right: 10px;
  flex-shrink: 0;
  border: 2px solid ${props => props.theme.border};
`;

const LeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const LeaderName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LeaderStats = styled.div`
  font-size: 11px;
  color: ${props => props.theme.subText};
  margin-top: 2px;
`;

const ScoreSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: 8px;
`;

const SuccessRate = styled.div<{ rate: number }>`
  font-size: 16px;
  font-weight: bold;
  color: ${props => {
    if (props.rate >= 70) return '#22C55E';
    if (props.rate >= 50) return '#F59E0B';
    return '#EF4444';
  }};
`;

const RecordText = styled.div`
  font-size: 10px;
  color: ${props => props.theme.subText};
`;

const ScoreTooltip = styled.div`
  font-size: 9px;
  color: ${props => props.theme.subText};
  margin-top: 1px;
  opacity: 0.8;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 24px;
  color: ${props => props.theme.subText};
  font-size: 14px;
`;

interface MobileLeaderboardProps {
  predictions: Prediction[];
  chartData: PriceDataPoint[];
  theme: Theme;
  maxEntries?: number;
  embedded?: boolean; // When true, removes container styling (for use in bottom sheet)
}

const MobileLeaderboard: React.FC<MobileLeaderboardProps> = ({
  predictions,
  chartData,
  theme,
  maxEntries = 10,
  embedded = false
}) => {
  // Calculate leaderboard using fair scoring algorithm
  const leaderboard = useMemo(() => {
    return calculateFairLeaderboard(predictions, chartData, maxEntries);
  }, [predictions, chartData, maxEntries]);

  const getMedal = (rank: number): string => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `${rank}`;
  };

  if (leaderboard.length === 0) {
    return (
      <LeaderboardContainer theme={theme} $embedded={embedded}>
        <LeaderboardHeader $hidden={embedded}>
          <LeaderboardTitle theme={theme}>
            🏆 Prediction Leaderboard
          </LeaderboardTitle>
        </LeaderboardHeader>
        <EmptyState theme={theme}>
          No resolved predictions yet
        </EmptyState>
      </LeaderboardContainer>
    );
  }

  return (
    <LeaderboardContainer theme={theme} $embedded={embedded}>
      <LeaderboardHeader $hidden={embedded}>
        <LeaderboardTitle theme={theme}>
          🏆 Prediction Leaderboard
          <LeaderboardSubtitle theme={theme}>
            Who called it right?
          </LeaderboardSubtitle>
        </LeaderboardTitle>
      </LeaderboardHeader>
      
      {leaderboard.map((entry, index) => {
        const rank = index + 1;
        return (
          <LeaderRow key={entry.name} rank={rank} theme={theme}>
            <RankBadge rank={rank} theme={theme}>
              {getMedal(rank)}
            </RankBadge>
            
            {entry.profileImage ? (
              <ProfileImage src={entry.profileImage} alt={entry.name} theme={theme} />
            ) : (
              <ProfilePlaceholder theme={theme}>
                {entry.name.charAt(0).toUpperCase()}
              </ProfilePlaceholder>
            )}
            
            <LeaderInfo>
              <LeaderName theme={theme}>{entry.name}</LeaderName>
              <LeaderStats theme={theme}>
                ✅ {entry.successes} | ❌ {entry.misses} | ⏳ {entry.pending}
              </LeaderStats>
            </LeaderInfo>
            
            <ScoreSection>
              <SuccessRate rate={entry.displayScore} theme={theme}>
                {entry.displayScore}%
              </SuccessRate>
              <RecordText theme={theme}>
                {entry.successes}/{entry.successes + entry.misses} raw
              </RecordText>
              {(entry.proximityCredits > 0 || entry.directionalBonuses > 0) && (
                <ScoreTooltip theme={theme}>
                  +{((entry.proximityCredits + entry.directionalBonuses) * 10).toFixed(0)} bonus
                </ScoreTooltip>
              )}
            </ScoreSection>
          </LeaderRow>
        );
      })}
    </LeaderboardContainer>
  );
};

export default MobileLeaderboard;
